/**
 * What we owe — Reports step 2 (DESIGN-reports.md; Rob's decisions 09/10/2026).
 *
 * Untaken annual leave, who may lose days at the end of the leave year and how many working
 * days they have left to take them, carried-over days and their use-by date, and what each
 * leaver was settled at. Always "as of today, this leave year" (decision H) — no period
 * picker. Every figure is worked out on the server (services/report_owe.py); this page only
 * draws what it is given.
 *
 * ⚠️ DAYS FOR EVERYONE, PLUS HOURS FOR ANYONE WHO BOOKS IN HOURS (Rob, 2b): a small grey
 * line under the days, so their row matches what they see on their own balance. The
 * download has both for everybody.
 *
 * ⚠️ The four headline figures FILTER the list (decision J); clicking a person opens the
 * bookings behind their numbers.
 *
 * ⚠️ TAKEN AND BOOKED AHEAD (LAUNCH-CHECKLIST H5, the allowance report — Rob 20/10/2026,
 * mock-up `Claude outputs/what-we-owe-mock-v2.html`): "Booked" is shown as two columns,
 * taken up to and including today and booked ahead from tomorrow. The server splits them
 * (services/report_owe.py); the drill-down marks each booking and draws a "Today" line.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { reportsApi, apiErrorMessage, saveBlobResponse } from '../../lib/api';
import { formatDateGB } from '../../lib/dates';
import { formatMinutes } from '../../lib/leaveUnits';
import { fmtToil } from '../../lib/toil';
import {
  ReportFrame, Card, Chip, PillSelect, DownloadMenu, PrintButton, NoAccess, InfoTip, fmtDays, HEX,
  ScrollTable,
} from '../../components/reports/ReportUi';

const PAGE_DOWNLOADS = [
  { value: 'xlsx', label: 'Excel (people and leavers)' },
  { value: 'csv_people', label: 'CSV: people' },
  { value: 'csv_leavers', label: 'CSV: leavers' },
];

const days = (n) => `${fmtDays(n)} ${Number(n) === 1 ? 'day' : 'days'}`;

/** A figure in days, with the hours underneath for anyone who books in hours (2b). */
const Fig = ({ fig, unit, className = '' }) => {
  if (!fig) return <span className="text-hive-muted">–</span>;
  return (
    <span className={className}>
      {fmtDays(fig.days)}
      {unit === 'hours' && fig.minutes !== null && fig.minutes !== undefined && (
        <span className="block text-[11px] font-normal text-hive-muted dark:text-muted-foreground">
          {formatMinutes(fig.minutes)}
        </span>
      )}
    </span>
  );
};

const Stat = ({ label, value, sub, navy = false, active, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid} className="text-left">
    <Card className={`h-full transition-shadow hover:shadow-[0_10px_30px_rgba(19,31,60,.10)] ${
      navy ? 'relative overflow-hidden !bg-hive-navy !border-hive-navy text-white dark:!bg-hive-navy' : ''} ${
      active ? 'ring-2 ring-hive-orange' : ''}`}>
      {navy && <div className="absolute -right-5 -top-4 w-24 h-24 bg-hive-orange opacity-[.12]" style={{ clipPath: HEX }} />}
      <p className={`text-[12.5px] font-bold ${navy ? 'text-hive-muted-cool' : 'text-hive-muted dark:text-muted-foreground'}`}>{label}</p>
      <p className="font-poppins text-[28px] font-bold leading-tight">{value}</p>
      {sub && <p className={`text-xs ${navy ? 'text-hive-muted-cool' : 'text-hive-muted dark:text-muted-foreground'}`}>{sub}</p>}
    </Card>
  </button>
);

/** The carried-over column: what came over, and its state. */
const CarryCell = ({ carry }) => {
  if (!carry) return <span className="text-hive-muted">–</span>;
  if (carry.state === 'expired') {
    return <Chip>{fmtDays(carry.carried_days)} · {fmtDays(carry.expired_days)} expired {formatDateGB(carry.expired_on).slice(0, 5)}</Chip>;
  }
  if ((carry.to_use_days || 0) > 0) {
    return <Chip tone="pending">{fmtDays(carry.carried_days)} · {fmtDays(carry.to_use_days)} to use by {formatDateGB(carry.use_by).slice(0, 5)}</Chip>;
  }
  return <Chip tone="success">{fmtDays(carry.carried_days)} · all used</Chip>;
};

/** Each line in a person's bookings: taken, booked ahead, or waiting (H5). */
const WHEN = {
  taken: { tone: 'success', label: 'Taken' },
  ahead: { tone: 'info', label: 'Booked ahead' },
  waiting: { tone: 'pending', label: 'Waiting' },
};

const WhatWeOwePage = () => {
  const [filters, setFilters] = useState({ department_id: '', location_id: '' });
  const [show, setShow] = useState('all');       // all | lose | cant_fit | leavers
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [drill, setDrill] = useState(null);      // a person row
  const [drillData, setDrillData] = useState(null);
  const [busy, setBusy] = useState(null);

  const params = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportsApi.owe(params);
      setData(res.data);
      setForbidden(false);
    } catch (err) {
      if (err?.response?.status === 403) setForbidden(true);
      else toast.error(apiErrorMessage(err, 'Could not load the report'));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setDrill(null); setDrillData(null); }, [params]);

  useEffect(() => {
    if (!drill) return undefined;
    let cancelled = false;
    setDrillData(null);
    (async () => {
      try {
        const res = await reportsApi.owePerson(drill.user_id);
        if (!cancelled) setDrillData(res.data);
      } catch (err) {
        if (!cancelled) toast.error(apiErrorMessage(err, 'Could not load their bookings'));
      }
    })();
    return () => { cancelled = true; };
  }, [drill]);

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const res = await reportsApi.exportOwe({ ...params, format: fmt });
      const name = saveBlobResponse(res, `what-we-owe.${fmt === 'xlsx' ? 'xlsx' : 'csv'}`);
      toast.success(`Downloaded ${name}`);
    } catch (err) {
      let message = 'Could not build the download';
      try {
        const text = await err?.response?.data?.text?.();
        if (text) message = JSON.parse(text).detail || message;
      } catch { /* keep the fallback */ }
      toast.error(message);
    } finally {
      setBusy(null);
    }
  };

  if (forbidden) return <ReportFrame><NoAccess /></ReportFrame>;

  const t = data?.totals;
  const rules = data?.rules || {};
  const yearEnd = data ? formatDateGB(data.leave_year.end) : '';
  const carryOn = !!rules.carry_over;
  const showCarry = carryOn || (data?.people || []).some((r) => r.carry);
  const people = (data?.people || []).filter((r) => {
    if (show === 'lose') return (r.may_lose?.days || 0) > 0;
    if (show === 'cant_fit') return r.cant_fit;
    return true;
  });
  const toggle = (k) => setShow((s) => (s === k ? 'all' : k));
  // Every column after "Person": allowance, taken, booked ahead, waiting, untaken, working
  // days left, may be lost — plus the two carry-over columns when they show.
  const figureColumns = 7 + (showCarry ? 1 : 0) + (carryOn ? 1 : 0);
  // TOIL owed (DESIGN-toil.md J, assumption 10) — while TOIL is on, or anybody still has some.
  const showToil = !!rules.toil;
  const toilCell = (m, last) => (
    <td className={`text-right ${last ? 'pl-2' : 'px-2'} tabular-nums ${m < 0 ? 'text-hive-danger-fg font-bold' : ''}`}>
      {m === null || m === undefined ? '–' : fmtToil(m)}
    </td>
  );
  // The drill-down's "Today" line goes above the first booking that starts after today.
  const todayIso = drillData?.today;
  const firstAhead = (drillData?.bookings || []).findIndex((b) => todayIso && b.from > todayIso);

  return (
    <ReportFrame>
      <p className="text-[13px] text-hive-muted dark:text-muted-foreground mb-1 print:hidden">
        <Link to="/reports" className="hover:underline">Reports</Link> › <b className="text-hive-navy dark:text-foreground">What we owe</b>
      </p>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-poppins text-[26px] font-semibold">What we owe</h1>
          <p className="text-sm text-hive-muted dark:text-muted-foreground">
            {data ? `${data.scope.label} · as of today, ${formatDateGB(data.as_of)} · ${data.period.label} (${data.period.range}) · annual leave` : 'Loading…'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PrintButton />
          <DownloadMenu options={PAGE_DOWNLOADS} busy={!!busy} onPick={download} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3 print:hidden">
        <PillSelect label="Department" value={filters.department_id} onChange={(v) => setFilters((f) => ({ ...f, department_id: v }))}>
          <option value="">All departments</option>
          {(data?.filters.departments || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </PillSelect>
        <PillSelect label="Location" value={filters.location_id} onChange={(v) => setFilters((f) => ({ ...f, location_id: v }))}>
          <option value="">All locations</option>
          {(data?.filters.locations || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </PillSelect>
      </div>

      <div className={`grid grid-cols-2 lg:grid-cols-4 gap-3.5 my-4 ${loading ? 'opacity-60' : ''}`}>
        <Stat navy testid="owe-stat-untaken" active={show === 'all'} onClick={() => setShow('all')}
              label="Untaken annual leave" value={t ? days(t.untaken.days) : '–'}
              sub={t ? `${t.people} ${t.people === 1 ? 'person' : 'people'} · left after everything taken, booked or waiting${
                t.over.length ? ` · ${t.over.length} over their allowance, not counted` : ''}` : ''} />
        <Stat testid="owe-stat-lose" active={show === 'lose'} onClick={() => toggle('lose')}
              label={`May be lost by ${yearEnd}`} value={t ? days(t.may_lose.days) : '–'}
              sub={!data ? '' : !carryOn ? 'carry-over is off, so nothing carries into next year'
                : `over the ${fmtDays(rules.cap_days)}-day carry-over limit${
                  t.may_lose_at_use_by ? ` · incl. ${fmtDays(t.may_lose_at_use_by)} carried-over days due earlier` : ''}`} />
        <Stat testid="owe-stat-cantfit" active={show === 'cant_fit'} onClick={() => toggle('cant_fit')}
              label="Can't all be taken" value={t ? `${t.cant_fit} ${t.cant_fit === 1 ? 'person' : 'people'}` : '–'}
              sub="more days left than working days to take them" />
        <Stat testid="owe-stat-leavers" active={show === 'leavers'} onClick={() => toggle('leavers')}
              label="Leavers this year" value={t ? `${t.leavers_owed > 0 ? '+' : ''}${days(t.leavers_owed)}` : '–'}
              sub={t ? `owed to ${t.leavers_owed_people} ${t.leavers_owed_people === 1 ? 'person' : 'people'}${
                t.leavers_owe_back ? ` · ${days(t.leavers_owe_back)} owed back by ${t.leavers_owe_back_people}` : ' · nobody owes any back'}` : ''} />
      </div>

      {show !== 'leavers' && (
        <Card className={loading ? 'opacity-60' : ''}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-poppins font-semibold text-[17px]">
              People{show === 'lose' ? ' who may lose days' : show === 'cant_fit' ? " who can't take it all" : ''}
            </p>
            <p className="text-[12.5px] text-hive-muted dark:text-muted-foreground">Most at risk first · click a person to see the bookings behind their numbers</p>
          </div>
          <ScrollTable>
            <table className="w-full text-[13.5px] mt-2" data-testid="owe-people">
              <thead>
                <tr className="text-[12px] text-hive-muted dark:text-muted-foreground border-b border-hive-line dark:border-border">
                  <th className="text-left font-bold py-2 pr-2">Person</th>
                  <th className="text-right font-bold px-2">Allowance</th>
                  <th className="text-right font-bold px-2">Taken</th>
                  <th className="text-right font-bold px-2 whitespace-nowrap">Booked ahead</th>
                  <th className="text-right font-bold px-2">Waiting</th>
                  <th className="text-right font-bold px-2 whitespace-nowrap">
                    Untaken{' '}
                    <InfoTip label="About untaken">
                      The allowance, minus what's been <b>taken</b> (up to and including today), minus what's
                      <b> booked ahead</b>, minus anything <b>waiting</b> for approval. A waiting request comes off
                      straight away, the same way everyone's own balance works in HolidayHive. If it's declined,
                      the days come back.
                    </InfoTip>
                  </th>
                  <th className="text-right font-bold px-2 whitespace-nowrap">
                    Working days left{' '}
                    <InfoTip label="About working days left">
                      The working days each person still has between tomorrow and {yearEnd}, on their
                      <b> own working week</b>. Bank holidays don't count, and nor do days they've already
                      booked off (approved or waiting). If someone has more leave left than this, they
                      can't take it all — that's the "Can't all be taken" figure above.
                    </InfoTip>
                  </th>
                  {showCarry && <th className="text-right font-bold px-2">Carried over</th>}
                  {carryOn && <th className="text-right font-bold px-2">Carries into next year</th>}
                  <th className="text-right font-bold pl-2">May be lost</th>
                  {showToil && (
                    <th className="text-right font-bold pl-2 whitespace-nowrap">
                      TOIL owed{' '}
                      <InfoTip label="About TOIL owed">
                        Time in lieu earned, less what's been <b>taken</b> up to and including today, less anything
                        expired or corrected away. Time in lieu booked for later is still owed until the day comes.
                        Always hours and minutes.
                      </InfoTip>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {people.map((r) => (
                  <tr key={r.user_id}
                      onClick={() => !r.no_balance && setDrill(r)}
                      className={`border-b border-hive-line-soft dark:border-border cursor-pointer hover:bg-hive-tile dark:hover:bg-muted ${
                        drill?.user_id === r.user_id ? 'bg-hive-pending-bg dark:bg-muted' : ''}`}>
                    <td className="py-2 pr-2">
                      <b>{r.name}</b>
                      <span className="block text-[12px] text-hive-muted dark:text-muted-foreground">{r.department || 'No department'}</span>
                    </td>
                    {r.no_balance ? (
                      <td colSpan={figureColumns} className="text-right text-hive-muted px-2">No balance for this leave year</td>
                    ) : (
                      <>
                        <td className="text-right px-2 tabular-nums"><Fig fig={r.allowance} unit={r.display_unit} /></td>
                        <td className="text-right px-2 tabular-nums"><Fig fig={r.taken} unit={r.display_unit} /></td>
                        <td className="text-right px-2 tabular-nums"><Fig fig={r.ahead} unit={r.display_unit} /></td>
                        <td className="text-right px-2 tabular-nums"><Fig fig={r.waiting} unit={r.display_unit} /></td>
                        <td className="text-right px-2 tabular-nums">
                          <Fig fig={r.untaken} unit={r.display_unit} className={r.over ? 'text-hive-danger-fg font-bold' : ''} />
                        </td>
                        <td className="text-right px-2 tabular-nums">
                          {r.working_days_left === null || r.working_days_left === undefined ? '–' : fmtDays(r.working_days_left)}
                        </td>
                        {showCarry && <td className="text-right px-2"><CarryCell carry={r.carry} /></td>}
                        {carryOn && <td className="text-right px-2 tabular-nums">{r.over ? '–' : fmtDays(r.carries?.days)}</td>}
                        <td className="text-right pl-2">
                          {r.over ? <Chip tone="danger">{fmtDays(r.over)} over</Chip>
                            : (r.may_lose?.days || 0) > 0
                              ? <Chip tone={r.cant_fit ? 'danger' : 'pending'}>{fmtDays(r.may_lose.days)}</Chip>
                              : <Chip>0</Chip>}
                        </td>
                      </>
                    )}
                    {showToil && toilCell(r.toil_owed, true)}
                  </tr>
                ))}
                {!people.length && (
                  <tr><td colSpan={figureColumns + 1 + (showToil ? 1 : 0)} className="py-6 text-center text-hive-muted">Nobody here.</td></tr>
                )}
              </tbody>
            </table>
          </ScrollTable>

          {drill && (
            <div className="mt-3.5 rounded-2xl border border-hive-line dark:border-border bg-hive-tile dark:bg-card p-4" data-testid="owe-drill">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-bold">{drill.name} · {data?.period.label}</p>
                  {drillData && (
                    <p className="font-poppins font-semibold mt-1">
                      {fmtDays(drillData.allowance)} allowance − {fmtDays(drillData.taken)} taken − {fmtDays(drillData.ahead)} booked ahead
                      {' '}− {fmtDays(drillData.waiting)} waiting = {fmtDays(drillData.untaken)} untaken
                    </p>
                  )}
                </div>
                <button onClick={() => setDrill(null)} aria-label="Close"><X className="w-4 h-4" /></button>
              </div>
              {!drillData ? <p className="text-sm text-hive-muted mt-2">Loading…</p> : (
                <table className="w-full text-[13px] mt-2">
                  <thead>
                    <tr className="text-[12px] text-hive-muted border-b border-hive-line dark:border-border">
                      <th className="text-left font-bold py-1.5">Dates</th>
                      <th className="text-left font-bold">Leave type</th>
                      <th className="text-left font-bold">Status</th>
                      <th className="text-right font-bold">Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drillData.opening_days > 0 && (
                      <tr className="border-b border-hive-line-soft dark:border-border">
                        <td className="py-1.5" colSpan={2}>Days already taken before HolidayHive (typed in at setup)</td>
                        <td><Chip tone="success">Taken</Chip></td>
                        <td className="text-right tabular-nums">{fmtDays(drillData.opening_days)}</td>
                      </tr>
                    )}
                    {drillData.bookings.map((b, i) => (
                      <React.Fragment key={i}>
                      {i === firstAhead && (
                        <tr data-testid="owe-drill-today">
                          <td colSpan={4} className="pt-2 pb-1 text-[12px] font-extrabold text-hive-orange border-b-2 border-hive-orange">
                            Today, {formatDateGB(todayIso)}
                          </td>
                        </tr>
                      )}
                      <tr className="border-b border-hive-line-soft dark:border-border">
                        <td className="py-1.5">
                          {formatDateGB(b.from)}{b.to !== b.from ? ` – ${formatDateGB(b.to)}` : ''}
                          {b.end_type === 'lunchtime' && b.from === b.to ? ' (morning)' : ''}
                          {b.start_type === 'afternoon' && b.from === b.to ? ' (afternoon)' : ''}
                          {b.booking_from && (
                            <span className="block text-[11.5px] text-hive-muted">part of {formatDateGB(b.booking_from)} – {formatDateGB(b.booking_to)}</span>
                          )}
                        </td>
                        <td>{b.leave_type}</td>
                        <td><Chip tone={(WHEN[b.when] || {}).tone || 'grey'}>{(WHEN[b.when] || {}).label || b.status}</Chip></td>
                        <td className="text-right tabular-nums">{fmtDays(b.days)}</td>
                      </tr>
                      </React.Fragment>
                    ))}
                    {!drillData.bookings.length && !drillData.opening_days && (
                      <tr><td colSpan={4} className="py-3 text-hive-muted">Nothing booked this leave year.</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          )}

          <p className="text-[12.5px] text-hive-muted dark:text-muted-foreground mt-3">
            Counted from the bookings, at what each one cost when it was made. Taken is everything approved up to and
            including today, plus any days typed in at setup; booked ahead is approved from tomorrow. The allowance
            includes bank holidays given on top and any carried-over days. Someone over their allowance shows as "over" and isn't counted in
            the untaken total. Annual leave only.
          </p>
        </Card>
      )}

      {(show === 'all' || show === 'leavers') && (
        <Card className={`mt-3.5 ${loading ? 'opacity-60' : ''}`}>
          <p className="font-poppins font-semibold text-[17px]">Leavers this leave year</p>
          <ScrollTable>
            <table className="w-full text-[13.5px] mt-2" data-testid="owe-leavers">
              <thead>
                <tr className="text-[12px] text-hive-muted dark:text-muted-foreground border-b border-hive-line dark:border-border">
                  <th className="text-left font-bold py-2">Person</th>
                  <th className="text-right font-bold px-2">Last day</th>
                  <th className="text-right font-bold px-2">Entitled to their last day</th>
                  <th className="text-right font-bold px-2 whitespace-nowrap">Taken or booked</th>
                  <th className="text-right font-bold pl-2">Owed to them</th>
                  {showToil && <th className="text-right font-bold pl-2 whitespace-nowrap">TOIL owed</th>}
                </tr>
              </thead>
              <tbody>
                {(data?.leavers || []).map((l) => (
                  <tr key={l.user_id} className="border-b border-hive-line-soft dark:border-border">
                    <td className="py-2">
                      <b>{l.name}</b>
                      <span className="block text-[12px] text-hive-muted">{l.status}{l.department ? ` · ${l.department}` : ''}</span>
                    </td>
                    <td className="text-right px-2">{formatDateGB(l.last_day)}</td>
                    <td className="text-right px-2 tabular-nums"><Fig fig={l.entitled} unit={l.display_unit} /></td>
                    <td className="text-right px-2 tabular-nums"><Fig fig={l.taken} unit={l.display_unit} /></td>
                    <td className="text-right pl-2">
                      <Chip tone={(l.owed.days || 0) < 0 ? 'danger' : (l.owed.days || 0) > 0 ? 'success' : 'grey'}>
                        {(l.owed.days || 0) > 0 ? '+' : ''}{fmtDays(l.owed.days)}
                      </Chip>
                    </td>
                    {showToil && toilCell(l.toil_owed, true)}
                  </tr>
                ))}
                {!(data?.leavers || []).length && (
                  <tr><td colSpan={showToil ? 6 : 5} className="py-4 text-center text-hive-muted">Nobody has left or is leaving this leave year.</td></tr>
                )}
              </tbody>
            </table>
          </ScrollTable>
          <p className="text-[12.5px] text-hive-muted dark:text-muted-foreground mt-3">
            What was settled when they left. A minus figure means they took more than they'd earned and owe leave back.
          </p>
        </Card>
      )}
    </ReportFrame>
  );
};

export default WhatWeOwePage;
